import { a as isError, g as getMediaErrorCodeName, b as isMediaErrorFatal, s as stringifyError, t as toError } from './error.js';

/**
 * Playback session error types. Carefully selected to convey what is certain
 * about the situation, while avoiding technical details which a user can't
 * relate to.
 */

let PlaybackSessionErrorType;
/**
 * Internal mapping from the error types above to "nice" messages.
 */

(function (PlaybackSessionErrorType) {
  PlaybackSessionErrorType["NO_PLAYBACK_NETWORK_ERROR"] = "NO_PLAYBACK_NETWORK_ERROR";
  PlaybackSessionErrorType["NO_PLAYBACK_DECODE_ERROR"] = "NO_PLAYBACK_DECODE_ERROR";
  PlaybackSessionErrorType["PLAYBACK_STOPPED_NETWORK_ERROR"] = "PLAYBACK_STOPPED_NETWORK_ERROR";
  PlaybackSessionErrorType["PLAYBACK_STOPPED_DECODE_ERROR"] = "PLAYBACK_STOPPED_DECODE_ERROR";
  PlaybackSessionErrorType["UNSUPPORTED_FORMAT_ERROR"] = "UNSUPPORTED_FORMAT_ERROR";
  PlaybackSessionErrorType["UNKNOWN_ERROR"] = "UNKNOWN_ERROR";
})(PlaybackSessionErrorType || (PlaybackSessionErrorType = {}));

var ErrorMessage;
/**
 * Error class used when the player is in the "broken" state.
 */

(function (ErrorMessage) {
  ErrorMessage["NO_PLAYBACK_NETWORK_ERROR"] = "Failed to load the media.";
  ErrorMessage["NO_PLAYBACK_DECODE_ERROR"] = "Failed to decode the media.";
  ErrorMessage["PLAYBACK_STOPPED_NETWORK_ERROR"] = "Failed to load more of the media.";
  ErrorMessage["PLAYBACK_STOPPED_DECODE_ERROR"] = "Failed to decode the next segment of media.";
  ErrorMessage["UNSUPPORTED_FORMAT_ERROR"] = "Unsupported media format.";
  ErrorMessage["UNKNOWN_ERROR"] = "Unknown error.";
})(ErrorMessage || (ErrorMessage = {}));

class PlaybackSessionError extends Error {
  constructor(type, cause, causeSummary, fatal = true) {
    super(ErrorMessage[type]);
    this.type = type;
    this.cause = cause;
    this.causeSummary = causeSummary;
    this.fatal = fatal;
    Object.setPrototypeOf(this, PlaybackSessionError.prototype);
    this.name = 'PlaybackSessionError';
  }

  static fromUnknownError(e, playbackStarted, context) {
    const error = toError(e, context);
    return new PlaybackSessionError(resolveErrorType(error, playbackStarted), error, isError(e) ? error.name : error.message);
  }

  static fromMediaError(error, playbackStarted) {
    const errorObject = {
      name: 'MediaError',
      message: error.message,
      code: error.code
    };
    return new PlaybackSessionError(resolveMediaErrorType(error, playbackStarted), errorObject, getMediaErrorCodeName(error), isMediaErrorFatal(error));
  }

  getCauseAsString() {
    return stringifyError(this.cause);
  }

}
/**
 * Resolve the PlaybackSessionErrorType of any Error object.
 */

function resolveErrorType(error, playbackStarted) {
  if (error.name === 'NetworkError') {
    return playbackStarted ? PlaybackSessionErrorType.PLAYBACK_STOPPED_NETWORK_ERROR : PlaybackSessionErrorType.NO_PLAYBACK_NETWORK_ERROR;
  }

  if (error.name === 'DecodeError') {
    return playbackStarted ? PlaybackSessionErrorType.PLAYBACK_STOPPED_DECODE_ERROR : PlaybackSessionErrorType.NO_PLAYBACK_DECODE_ERROR;
  }

  return PlaybackSessionErrorType.UNKNOWN_ERROR;
}
/**
 * Resolve the PlaybackSessionErrorType of a MediaError.
 */

function resolveMediaErrorType(error, playbackStarted) {
  if (error.code === error.MEDIA_ERR_NETWORK) {
    return playbackStarted ? PlaybackSessionErrorType.PLAYBACK_STOPPED_NETWORK_ERROR : PlaybackSessionErrorType.NO_PLAYBACK_NETWORK_ERROR;
  }

  if (error.code === error.MEDIA_ERR_DECODE) {
    return playbackStarted ? PlaybackSessionErrorType.PLAYBACK_STOPPED_DECODE_ERROR : PlaybackSessionErrorType.NO_PLAYBACK_DECODE_ERROR;
  }

  if (error.code === error.MEDIA_ERR_SRC_NOT_SUPPORTED) {
    return PlaybackSessionErrorType.UNSUPPORTED_FORMAT_ERROR;
  }

  return PlaybackSessionErrorType.UNKNOWN_ERROR;
}

export { PlaybackSessionErrorType as P, PlaybackSessionError as a };
